import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, ValidatorFn } from '@angular/forms';
import { Observable } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { I18nService } from "../../shared/services/i18n.service";

@Component({
    selector: 'tsl-select-autocomplete',
    templateUrl: './select-autocomplete.component.html'
})
export class SelectAutocompleteComponent implements OnInit, OnChanges {

    filteredvalue$: Observable<string[]>;
    selectedText: any;
    selectedValue: any;

    /** Form control of the input value */
    @Input('tsl-formControl') formControl = new FormControl('', this.enteredOptionValidator());

    /** Text of the label for the control */
    @Input('tsl-labelText') labelText: string = '';

    /** Placeholder for the input element */
    @Input('tsl-inputPlaceholder') inputPlaceholder: string = 'Start typing...';

    /** Error message for when the user hasn't select an option */
    @Input('tsl-errorMessage') errorMessage: string = 'Please make a selection';

    /** Collections of suggestion options to display to user */
    @Input('tsl-options') options: any[] = [];

    /** Name of the field to get the text to display to the user */
    @Input('tsl-textFieldName') textFieldName: string = '';

    /** Name of the value field to set the selected value when user clicks on option */
    @Input('tsl-valueFieldName') valueFieldName: string = '';

    /** Function to select the text of an option. Defaults to a function that returns the field from taxFieldName if provided. */
    @Input('tsl-textSelector') textSelector: (option: any) => string = opt => this.textFieldName ? opt[this.textFieldName] : opt;

    /** Function to select the value of an option. Defaults to a function that return the field from valueFieldName. */
    @Input('tsl-valueSelector') valueSelector: (option: any) => string = opt => this.valueFieldName ? opt[this.valueFieldName] : opt;

    /** Event to trigger when user selects a new value */
    @Output('tsl-selectionChanged') selectChanged: EventEmitter<{ text: any, value: any }> = new EventEmitter<{ text: any, value: any }>();

    constructor(
        public translate: I18nService,
    ) {}

    ngOnInit() {
        this.onClick = this.onClick.bind(this);
        this.filteredvalue$ = this.formControl.valueChanges.pipe(
            debounceTime(300),
            map(value => this.filterOptions(value))
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.formControl) {
            this.filteredvalue$ = this.formControl.valueChanges.pipe(
                debounceTime(300),
                map(value => this.filterOptions(value))
            );
        }

        if (changes.options.currentValue) {
            this.setValue(this.selectedValue);
        }
    }

    onClick() {
        if (!this.formControl.value) {
            this.formControl.setValue('', { emitEvent: true });
            this.formControl.updateValueAndValidity();
        }
    }

    /** Sets value and text */
    setValue(value: any, text?: any) {
        if (value) {
            this.selectedValue = value;

            //  Set text if provided
            if (text) {
                this.selectedText = text;
                this.formControl.setValue(text, { emitEvent: false });
            } else if (this.options) {

                //  Try to find option and set the text
                const existingOption = this.options.find(opt => this.valueSelector(opt) == this.selectedValue);
                if (existingOption) {
                    this.selectedText = this.textSelector(existingOption);
                    this.formControl.setValue(this.selectedText, { emitEvent: false });
                }
            }
        }
    }

    onSelection(tslOption: any) {
        this.selectedText = tslOption.viewValue;
        this.selectedValue = tslOption.value;
        this.formControl.setValue(this.selectedText);
        this.selectChanged.emit({ value: this.selectedValue, text: this.selectedText });
    }

    private filterOptions(value: string): string[] {
        if (value) {
            const filterValue = value.toLowerCase();
            return this.options?.filter(opt => this.textSelector(opt)?.toLowerCase().startsWith(filterValue));
        }

        return this.options;
    }

    /** Custom validator checking that the input value is included in one of the options */
    enteredOptionValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            if (this.options && this.options.length > 0) {
                if (!this.selectedText && !this.options.map(opt => this.textSelector(opt)).some(opt => opt == this.selectedText)) {
                    return { enteredOption: { value: control.value } }
                }
            }

            //  No error
            return null;
        };
    }
}